<!--
	Copyright 2023 HALBY
	See LICENSE file for details.
 -->

<script lang="ts">
	import { base, assets } from '$app/paths';
	import Image from '$lib/components/Image.svelte';
	import { getLang, t, getLangUrl } from '$lib/translations/translations';
	import { page } from '$app/stores';
	import { onMount } from 'svelte';
	import { getMaruFont } from '$lib/services/font-service';
	import { PUBLIC_BASE_PATH } from '$env/static/public';

	let maru_font: string | null = null;
	onMount(() => {
		maru_font = getMaruFont($page.url);
	});

	const headerLangButtons = [
		{
			lang: 'ja',
			text: '日本語'
		},
		{
			lang: 'zh-cn',
			text: '简体中文'
		},
		{
			lang: 'zh-tw',
			text: '繁體中文'
		}
	];

	function changeLanguage(e: MouseEvent, lang: string) {
		let { pathname } = $page.url;
		pathname = pathname.replace(new RegExp(PUBLIC_BASE_PATH), '');
		const url =
			$page.url.protocol +
			'//' +
			$page.url.host +
			PUBLIC_BASE_PATH +
			pathname.replace(/^\/[^/]+/, `/${lang}`);
		console.log(url);
		location.replace(url);
	}
</script>

<svelte:head>
	<meta property="og:site_name" content={$t('common.title')} />
	<meta property="og:description" content={$t('common.meta')} />
</svelte:head>

<header id="header" class="z-10 flex md:h-[110px] h-[70px] w-full justify-center">
	<div class="flex relative h-full w-full max-w-[1280px] justify-between px-[30px] py-0 box-border">
		<div class="flex h-full items-center">
			<!-- ロゴ -->
			<a class="" href="{getLangUrl($page.url)}/">
				<Image
					isTransparent={true}
					id="header-main-logo"
					src="{assets}/img/common/tomarigi_logo"
					class="block h-[50px] md:h-[60px] lg:h-[77px]"
					alt="logo"
				/>
			</a>
			<!-- LANGUAGE -->
			<div class="ml-4 mr-auto w-[118px] h-full flex relative group">
				<img
					src="{assets}/img/common/header/language01.svg"
					alt="language"
					class="w-full relative transition-opacity duration-300 opacity-100 group-hover:opacity-0"
				/>
				<img
					src="{assets}/img/common/header/language02.svg"
					alt="language"
					class="w-full absolute top-0 bottom-0 m-auto group-hover:block transition-opacity duration-300 opacity-0 group-hover:opacity-100"
				/>
				<div
					class="absolute top-1/2 left-1/2 transform -translate-x-1/2 mt-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
				>
					<!-- 追加で表示する要素 -->
					{#each headerLangButtons as { lang, text }, i}
						<button
							class="my-2 w-[195px] h-[47px] bg-dark-brown text-white rounded-full grid place-items-center font-header-jp text-[18.19px] font-bold header-lang-button"
							on:click={(e) => changeLanguage(e, lang)}
						>
							<div class="transform -translate-y-[2px]">{text}</div>
						</button>
					{/each}
				</div>
			</div>
		</div>
		<!-- とまりぎ亭とは、作家一覧 -->
		<div class="lg:flex items-center hidden">
			<div class="links flex flex-row text-[10px] mr-[40px] gap-[17px]">
				<a class="link-content no-underline" href="{getLangUrl($page.url)}/about">
					<h1 class="text-[24px] font-bold m-0 text-font-base {maru_font}">
						{$t('common.header.about.ja')}
					</h1>
					<p class="text-[12px] font-medium tracking-[0.8px] text-center text-accent font-base-jp">
						{$t('common.header.about.en')}
					</p>
				</a>
				<hr class="w-[3.5px] h-auto mx-[5px] my-[7.5px] bg-accent" />
				<a class="link-content no-underline" href="{getLangUrl($page.url)}/artists">
					<h1 class="text-[24px] font-bold m-0 text-font-base {maru_font}">
						{$t('common.header.artists.ja')}
					</h1>
					<p class="text-[12px] font-medium tracking-[0.8px] text-center text-accent font-base-jp">
						{$t('common.header.artists.en')}
					</p>
				</a>
			</div>
			<a
				class="request-button relative hidden md:flex w-[225.905px] h-[46px]"
				href="{getLangUrl($page.url)}/request"
			>
				<img src="{assets}/img/common/req_button.svg" alt="" />
				<div class="text-block absolute w-full h-full grid place-items-center">
					<p
						class="text-[22px] font-bold translate-x-[-21px] translate-y-[-2px] text-white font-maru-jp"
					>
						{$t('common.header.request')}
					</p>
				</div>
			</a>
		</div>
	</div>
</header>

<style lang="scss">
	@use '$lib/assets/css/mq-settings' as mq;
	@use '$lib/assets/css/variables' as var;

	header {
		box-shadow: 0px 0px 16px #351c1155;
	}
	:global(#header-main-logo) {
		@include var.hover-lighter-strong;
	}
	.left-block {
		:global(#header-main-logo) {
			max-height: 70%;
		}
	}
	.link-content {
		@include var.hover-lighter-strong;
	}
	.request-button {
		@include var.hover-lighter-strong;
	}
	.header-lang-button {
		@include var.hover-lighter-strong;
	}
</style>
